import React, { useCallback, useMemo, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { GET_CARDS } from "graphql/card";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { useSelectSearch } from "hooks/useSelectSearch";

export interface Card {
  id: string;
  cardNumber: string;
  nickname: string;
}

interface CardSearchAutoCompleteProps {
  title: string;
  card: Card | null;
  onSelectCard: (card: Card | null) => void;
  fullWidth?: boolean;
  loadContent?: boolean;
}

/** The label the field and the option row both show. */
export const cardLabel = (card: Card) => `${card.cardNumber}${card.nickname}`;

/**
 * The card picker. Server-searched, so the app owns the fetch and paging
 * through `useSelectSearch` and hands the Select back `options` + `loading`.
 *
 * `highlight` is the term the RESULTS came back with, not the live input: the
 * two differ while a request is in flight, and marking against the newer one
 * runs the bold ahead of the rows it explains.
 */
const CardSearchAutoComplete: React.FC<CardSearchAutoCompleteProps> = ({
  title,
  card,
  onSelectCard,
}) => {
  const client = useApolloClient();
  // Options carry only `{ id, label }`, but callers want the domain object
  // back, so every card this component has seen is kept by id.
  const seenRef = useRef(new Map<string, Card>());

  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const { data } = await client.query({
        query: GET_CARDS,
        variables: {
          searchTerm: query,
          cardStatus: ["ACTIVE", "FROZEN"],
          first: DEFAULT_PAGINATION_SIZE,
          after: after ?? undefined,
        },
        fetchPolicy: "network-only",
      });
      const connection = data?.cards;
      const cards: Card[] = (connection?.edges ?? []).map(
        ({
          node,
        }: {
          node: { id: string; cardNumber: string; nickname?: string };
        }) => ({
          id: node.id,
          cardNumber: node.cardNumber,
          nickname: node.nickname ? ` - ${node.nickname}` : "",
        }),
      );
      cards.forEach((c) => seenRef.current.set(c.id, c));
      return {
        options: cards.map((c) => ({ id: c.id, label: cardLabel(c) })),
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        endCursor: connection?.pageInfo?.endCursor ?? null,
      };
    },
    [client],
  );

  const seed = useMemo<SelectOption | null>(
    () => (card?.id ? { id: card.id, label: cardLabel(card) } : null),
    [card],
  );

  const search = useSelectSearch<SelectOption>({ fetchPage, seed });

  return (
    <Select
      label={title}
      options={search.options}
      loading={search.loading}
      highlight={search.query}
      onInputChange={search.onInputChange}
      onScrollEnd={search.onScrollEnd}
      value={card?.id ?? null}
      onChange={(id) => {
        if (!id) {
          search.reset();
          onSelectCard(null);
          return;
        }
        onSelectCard(seenRef.current.get(id) ?? card);
      }}
    />
  );
};

export default CardSearchAutoComplete;
